import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError, type CostsActivity, type CostsByRecording } from "@/lib/api";
import { formatCost } from "@/lib/format";
import { cn } from "@/lib/cn";

/**
 * UI-22 — Settings → Costs.
 *
 * One read (`GET /api/costs`), so the headline total and every breakdown below it come
 * from the same query and can never disagree (UI-9's rule, applied to spend). The month
 * is the viewer's own local calendar month — the browser's IANA zone travels with the
 * request, the same correction M17.2 made for a source's date.
 *
 * Owner or admin only: an ordinary member's request answers 403, and this page has
 * nothing else to fall back to.
 */
export function CostsSettings({ summary }: { summary: string }) {
  const { api } = useApp();
  const [month, setMonth] = useState(() => currentMonth());
  const [data, setData] = useState<CostsActivity | null>(null);
  const [problem, setProblem] = useState<{ message: string; forbidden: boolean } | null>(null);
  const [loading, setLoading] = useState(false);

  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const load = useCallback(async () => {
    setLoading(true);
    setProblem(null);

    try {
      setData(await api.getCostsActivity(month, tz));
    } catch (cause: unknown) {
      const forbidden = cause instanceof ApiError && cause.status === 403;
      setProblem({
        message:
          cause instanceof ApiError
            ? forbidden
              ? "Only an owner or admin can see what the organization spends."
              : cause.message
            : "The month's costs could not be loaded.",
        forbidden,
      });
    } finally {
      setLoading(false);
    }
  }, [api, month, tz]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="flex max-w-[960px] flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header>
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Costs
        </h1>
        <p className="mt-1 text-[length:var(--text-md)] text-[var(--text-secondary)]">{summary}</p>
      </header>

      <MonthPicker month={month} onChange={setMonth} trailingMonths={data?.trailingMonths ?? []} />

      {problem && (
        <p
          role="alert"
          className="rounded-[var(--radius-lg)] border border-[var(--warning)] bg-[var(--surface-glass)] px-[var(--space-4)] py-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--text-primary)]"
        >
          {problem.message}
        </p>
      )}

      {!problem && loading && !data && (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
      )}

      {!problem?.forbidden && data && (
        <>
          <TotalCard activity={data} />

          {data.totalCostUsd === null && data.unpricedCalls === 0 ? (
            <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
              Nothing was billed in this month.
            </p>
          ) : (
            <>
              <Breakdown title="By provider and model">
                <table className="w-full text-left text-[length:var(--text-sm)]">
                  <thead>
                    <tr className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
                      <th className="pb-[var(--space-2)] font-semibold">Provider</th>
                      <th className="pb-[var(--space-2)] font-semibold">Model</th>
                      <th className="pb-[var(--space-2)] text-right font-semibold">Calls</th>
                      <th className="pb-[var(--space-2)] text-right font-semibold">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byProviderAndModel.map((row) => (
                      <tr key={`${row.provider}-${row.model}`} className="border-t border-[var(--border-subtle)]">
                        <td className="py-[var(--space-2)] text-[var(--text-primary)]">{row.provider}</td>
                        <td className="py-[var(--space-2)] font-[family-name:var(--font-mono)] text-[var(--text-secondary)]">
                          {row.model}
                        </td>
                        <td className="py-[var(--space-2)] text-right text-[var(--text-secondary)]">{row.calls}</td>
                        <td className="py-[var(--space-2)] text-right text-[var(--text-primary)]">
                          <Cost costUsd={row.costUsd} unpriced={row.unpricedCalls} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Breakdown>

              <Breakdown title="By pipeline step">
                <table className="w-full text-left text-[length:var(--text-sm)]">
                  <thead>
                    <tr className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
                      <th className="pb-[var(--space-2)] font-semibold">Step</th>
                      <th className="pb-[var(--space-2)] text-right font-semibold">Calls</th>
                      <th className="pb-[var(--space-2)] text-right font-semibold">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byOperation.map((row) => (
                      <tr key={row.operation} className="border-t border-[var(--border-subtle)]">
                        <td className="py-[var(--space-2)] font-[family-name:var(--font-mono)] text-[var(--text-primary)]">
                          {row.operation}
                        </td>
                        <td className="py-[var(--space-2)] text-right text-[var(--text-secondary)]">{row.calls}</td>
                        <td className="py-[var(--space-2)] text-right text-[var(--text-primary)]">
                          <Cost costUsd={row.costUsd} unpriced={row.unpricedCalls} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Breakdown>

              <Breakdown title="By recording">
                {data.byRecording.length === 0 ? (
                  <EmptyRow />
                ) : (
                  <ul className="flex flex-col gap-[var(--space-2)]">
                    {data.byRecording.map((recording) => (
                      <RecordingRow key={recording.recordingId ?? "unknown"} recording={recording} />
                    ))}
                  </ul>
                )}
              </Breakdown>

              {data.synthesisBatches.length > 0 && (
                <Breakdown title="Synthesis batches">
                  <table className="w-full text-left text-[length:var(--text-sm)]">
                    <thead>
                      <tr className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
                        <th className="pb-[var(--space-2)] font-semibold">Ran</th>
                        <th className="pb-[var(--space-2)] text-right font-semibold">Insights</th>
                        <th className="pb-[var(--space-2)] text-right font-semibold">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.synthesisBatches.map((batch) => (
                        <tr key={batch.batchId} className="border-t border-[var(--border-subtle)]">
                          <td className="py-[var(--space-2)] text-[var(--text-secondary)]">
                            {new Date(batch.createdAt).toLocaleString()}
                          </td>
                          <td className="py-[var(--space-2)] text-right text-[var(--text-secondary)]">
                            {batch.insightCount}
                          </td>
                          <td className="py-[var(--space-2)] text-right text-[var(--text-primary)]">
                            <Cost costUsd={batch.costUsd} unpriced={0} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Breakdown>
              )}

              {data.other.length > 0 && (
                <Breakdown title="Not about a recording">
                  <table className="w-full text-left text-[length:var(--text-sm)]">
                    <thead>
                      <tr className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
                        <th className="pb-[var(--space-2)] font-semibold">Kind</th>
                        <th className="pb-[var(--space-2)] text-right font-semibold">Calls</th>
                        <th className="pb-[var(--space-2)] text-right font-semibold">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.other.map((row) => (
                        <tr key={row.kind} className="border-t border-[var(--border-subtle)]">
                          <td className="py-[var(--space-2)] text-[var(--text-primary)]">{KindLabel(row.kind)}</td>
                          <td className="py-[var(--space-2)] text-right text-[var(--text-secondary)]">{row.calls}</td>
                          <td className="py-[var(--space-2)] text-right text-[var(--text-primary)]">
                            <Cost costUsd={row.costUsd} unpriced={row.unpricedCalls} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Breakdown>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function parseMonth(month: string): { year: number; monthIndex: number } {
  const [yearPart, monthPart] = month.split("-");
  return { year: Number(yearPart), monthIndex: Number(monthPart) };
}

function shiftMonth(month: string, delta: number): string {
  const { year, monthIndex } = parseMonth(month);
  const shifted = new Date(year, monthIndex - 1 + delta, 1);
  return `${shifted.getFullYear()}-${String(shifted.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(month: string): string {
  const { year, monthIndex } = parseMonth(month);
  return new Date(year, monthIndex - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function MonthPicker({
  month,
  onChange,
  trailingMonths,
}: {
  month: string;
  onChange: (month: string) => void;
  trailingMonths: { month: string; costUsd: number | null }[];
}) {
  const totalsByMonth = useMemo(
    () => new Map(trailingMonths.map((m) => [m.month, m.costUsd])),
    [trailingMonths],
  );

  return (
    <div className="flex items-center gap-[var(--space-3)]">
      <button
        type="button"
        onClick={() => onChange(shiftMonth(month, -1))}
        aria-label="Previous month"
        className="rounded-[var(--radius-lg)] border border-[var(--border-default)] p-[8px] text-[var(--text-secondary)] transition hover:bg-[var(--surface-2)]"
      >
        <ChevronLeft size={14} strokeWidth={2} />
      </button>

      <span className="min-w-[10ch] text-center text-[length:var(--text-md)] font-semibold text-[var(--text-primary)]">
        {monthLabel(month)}
      </span>

      <button
        type="button"
        onClick={() => onChange(shiftMonth(month, 1))}
        disabled={month >= currentMonth()}
        aria-label="Next month"
        className="rounded-[var(--radius-lg)] border border-[var(--border-default)] p-[8px] text-[var(--text-secondary)] transition hover:bg-[var(--surface-2)] disabled:opacity-40"
      >
        <ChevronRight size={14} strokeWidth={2} />
      </button>

      {totalsByMonth.size > 0 && (
        <span className="ml-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
          {trailingMonths.length} month(s) of history
        </span>
      )}
    </div>
  );
}

function TotalCard({ activity }: { activity: CostsActivity }) {
  return (
    <section className="flex items-baseline justify-between rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-6)]">
      <div>
        <p className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
          Total, this month
        </p>
        <p className="mt-1 text-[length:var(--text-3xl)] font-extrabold text-[var(--text-primary)]">
          {activity.totalCostUsd === null ? "—" : formatCost(activity.totalCostUsd)}
        </p>
      </div>

      {activity.unpricedCalls > 0 && (
        <p className="max-w-[32ch] text-right text-[length:var(--text-xs)] text-[var(--text-muted)]">
          {activity.unpricedCalls} call(s) used a model with no published price and are not counted above.
        </p>
      )}
    </section>
  );
}

function Cost({ costUsd, unpriced }: { costUsd: number | null; unpriced: number }) {
  if (costUsd === null && unpriced === 0) return <span className="text-[var(--text-muted)]">—</span>;

  return (
    <span>
      {costUsd !== null ? formatCost(costUsd) : "—"}
      {unpriced > 0 && (
        <span className="ml-[6px] text-[length:var(--text-2xs)] text-[var(--text-muted)]">
          (+{unpriced} unpriced)
        </span>
      )}
    </span>
  );
}

function RecordingRow({ recording }: { recording: CostsByRecording }) {
  const [open, setOpen] = useState(false);

  return (
    <li className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)]">
      <div className="flex items-center gap-[var(--space-3)] px-[var(--space-4)] py-[var(--space-3)]">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
          disabled={recording.meetings.length === 0}
          className="shrink-0 text-[var(--text-muted)] disabled:opacity-30"
        >
          <ChevronRight
            size={14}
            strokeWidth={2}
            aria-hidden
            className={cn("transition-transform", open && "rotate-90")}
          />
        </button>

        {recording.deleted || recording.recordingId === null ? (
          <span className="min-w-0 flex-1 truncate text-[length:var(--text-sm)] text-[var(--text-muted)]">
            Deleted recording
          </span>
        ) : (
          <Link
            to={`/recordings/${recording.recordingId}`}
            className="min-w-0 flex-1 truncate text-[length:var(--text-sm)] text-[var(--text-primary)] hover:underline"
          >
            {recording.title ?? "Untitled recording"}
          </Link>
        )}

        <span className="shrink-0 text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
          {formatCost(recording.costUsd)}
        </span>
      </div>

      {open && recording.meetings.length > 0 && (
        <ul className="flex flex-col gap-[6px] border-t border-[var(--border-subtle)] px-[var(--space-4)] py-[var(--space-3)] pl-[calc(var(--space-4)+22px)]">
          {recording.meetings.map((meeting) => (
            <li key={meeting.meetingId ?? "unknown"} className="flex items-center justify-between gap-[var(--space-3)]">
              <span className="min-w-0 truncate text-[length:var(--text-xs)] text-[var(--text-secondary)]">
                {meeting.deleted || meeting.meetingId === null
                  ? "Deleted meeting"
                  : (meeting.title ?? "Untitled meeting")}
              </span>
              <span className="shrink-0 text-[length:var(--text-xs)] text-[var(--text-secondary)]">
                {formatCost(meeting.costUsd)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function Breakdown({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-[var(--space-3)] rounded-[var(--radius-xl)] border border-[var(--border-subtle)] p-[var(--space-6)]">
      <h2 className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">{title}</h2>
      {children}
    </section>
  );
}

function EmptyRow() {
  return <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">Nothing this month.</p>;
}

function KindLabel(kind: string): string {
  return { chat: "Chat", synthesis: "Synthesis", sweep: "Sweeps", org: "Subject summaries" }[kind] ?? kind;
}
